import { COMPANY_ADDRESS, COMPANY_NAME, SUPPORT_EMAIL } from '../../../siteConfig';

/**
 * Mission and vision copy, verbatim from the "Mission Vision Statemeant.pages"
 * document. Shared here because the About Us page repeats the long-form
 * statements alongside the founder story.
 */
export const MISSION_STATEMENT =
    'At Abayatique, our mission is to celebrate and elevate the abaya as a timeless symbol of beauty, empowerment, and cultural expression. We provide a seamless platform where designers and customers connect through curated details, personalized searches, and a user-friendly experience. By embracing diversity and telling the stories behind every design, we aim to make the abaya a global fashion icon accessible to women of all cultures and backgrounds.';

export const MISSION_SHORT =
    'Empowering women through elegant, expressive abayas — connecting designers and customers in a seamless, inspiring experience.';

export const VISION_STATEMENT =
    'We envision a world where the abaya transcends boundaries — no longer defined solely by tradition, but reimagined as a statement of elegance, individuality, and strength. Through Abayatique, we aspire to build an inclusive community that celebrates craftsmanship, empowers women, and inspires a new generation to see the abaya not just as clothing, but as an experience and an art form.';

export const VISION_SHORT =
    'Redefining the abaya as a global fashion icon for all cultures — where tradition meets individuality and flow becomes power.';

export const MISSION_STATEMENT_AR =
    'في عبايتك Abayatique، نهدف إلى تمكين المرأة من خلال تقديم عبايات تجمع بين الجمال، والرقي، والتعبير عن الذات. نوفّر منصة سلسة تربط بين المصممين والعملاء، تتيح لهم الوصول إلى تصاميم مخصصة وتفاصيل دقيقة، ضمن تجربة فريدة تُبرز روعة العباية وأصالتها.';

export const MISSION_SHORT_AR =
    'تمكين المرأة من خلال عبايات أنيقة ومعبرة، ومنصة تربط بين المصممين والعملاء بتجربة سلسة وملهمة.';

export const VISION_STATEMENT_AR =
    'نطمح إلى أن تصبح العباية رمزًا عالميًا للأناقة والقوة، يتجاوز الحدود الثقافية والدينية. من خلال Abayatique، نبني مجتمعًا شاملاً يحتفي بالإبداع النسائي، ويعيد تعريف العباية كفن وتجربة تعكس الشخصية والجمال الداخلي.';

export const VISION_SHORT_AR =
    'إعادة تعريف العباية كرمز عالمي للموضة، حيث يلتقي التراث بالأناقة وتتحول الانسيابية إلى قوة.';

/**
 * Content for every footer link, keyed by URL slug.
 *
 * `sections` is an array of { heading?, paragraphs[] } — InfoPage renders them
 * in order. Copy here is standard pre-launch boilerplate; have it reviewed
 * before it becomes a legal commitment.
 */
export const INFO_PAGES = {
    'contact-us': {
        title: 'Contact Us',
        sections: [
            {
                paragraphs: [
                    `We would love to hear from you. Whether you have a question about a seller on our platform, an upcoming order, a customisation request, or a partnership idea, the ${COMPANY_NAME} team is here to help.`,
                    `Email us at ${SUPPORT_EMAIL} and we will respond within two business days.`,
                    `Our registered address is ${COMPANY_ADDRESS}.`,
                ],
            },
            {
                heading: 'Customer Support Hours',
                paragraphs: [
                    'Sunday to Thursday, 9:00 AM to 6:00 PM Gulf Standard Time. Messages received outside these hours are answered on the next working day.',
                ],
            },
        ],
    },

    'track-order': {
        title: 'Track Order',
        sections: [
            {
                paragraphs: [
                    `${COMPANY_NAME} has not opened for orders yet, so there is nothing to track at the moment.`,
                    'Once we launch, every order confirmation email will include a tracking number and a direct link to follow your parcel from the seller who made it to your door — whichever designer on our platform you ordered from.',
                    `Join our newsletter on the home page to be notified the moment ordering opens. If you believe you have an order with us already, write to ${SUPPORT_EMAIL} and we will look into it.`,
                ],
            },
        ],
    },

    'returns-and-refunds': {
        title: 'Returns & Refunds',
        sections: [
            {
                paragraphs: [
                    `We want you to love what you receive. If something is not right, ${COMPANY_NAME} accepts returns on eligible items within 14 days of delivery.`,
                ],
            },
            {
                heading: 'Eligibility',
                paragraphs: [
                    'Items must be unworn, unwashed and undamaged, with all original tags and packaging intact. For hygiene reasons, innerwear and pierced jewellery cannot be returned. Items bought during a clearance sale are final sale unless they arrive faulty.',
                ],
            },
            {
                heading: 'How to Start a Return',
                paragraphs: [
                    `Email ${SUPPORT_EMAIL} with your order number and the reason for the return. We will confirm your request and share collection instructions.`,
                ],
            },
            {
                heading: 'Refunds',
                paragraphs: [
                    'Approved refunds are issued to your original payment method within 7 to 10 business days of us receiving and inspecting the item. Original shipping charges are non-refundable except where the item was faulty or incorrectly sent.',
                ],
            },
        ],
    },

    faq: {
        title: "FAQ's",
        sections: [
            {
                heading: `When does ${COMPANY_NAME} launch?`,
                paragraphs: [
                    'We are bringing our first designers and sellers onto the platform now. Subscribe to the newsletter on our home page and you will be among the first to know when the marketplace opens.',
                ],
            },
            {
                heading: 'Where do you ship?',
                paragraphs: [
                    'At launch we will ship across the United Arab Emirates, with wider GCC delivery to follow shortly after.',
                ],
            },
            {
                heading: 'What sizes do you carry?',
                paragraphs: [
                    'Every seller lists a full size range, and each product page carries a detailed size guide with measurements so you can choose with confidence. You can also skip standard sizing entirely and give your own measurements — every order on the platform can be customised.',
                ],
            },
            {
                heading: 'How do I care for my abaya?',
                paragraphs: [
                    'Care instructions are printed on each garment label and repeated on the product page. As a general rule we recommend gentle cold washing or dry cleaning to protect the fabric and finish.',
                ],
            },
            {
                heading: 'I have a question that is not answered here.',
                paragraphs: [`Email us at ${SUPPORT_EMAIL} and we will get back to you.`],
            },
        ],
    },

    careers: {
        title: 'Career',
        sections: [
            {
                paragraphs: [
                    `${COMPANY_NAME} is a young team building a marketplace for modest fashion — connecting abaya designers and sellers with women who want something made for them. We are not actively hiring while we prepare for launch, but we are always glad to meet people who share that outlook.`,
                    `If you work in design, production, merchandising, photography or e-commerce and would like to be considered for future openings, send your CV and portfolio to ${SUPPORT_EMAIL} with the role you have in mind in the subject line.`,
                ],
            },
        ],
    },

    'about-us': {
        title: 'About Us',
        sections: [
            {
                heading: 'Our Story',
                paragraphs: [
                    'Abayatique was founded by two women from different corners of the world — Giana, a bold and inspiring entrepreneur from the Dominican Republic, and Noor, a passionate psychology major and mother from Saudi Arabia who has always cherished the elegance of abayas. Though we come from different cultures, we share a deep belief in the beauty, strength, and individuality of women — and in the powerful role fashion can play in expressing that. Together, our love for modest fashion and meaningful designs helped Abayatique to be born in Dubai — a city that reflects our values of diversity, elegance and most importantly innovation.',
                    'Together, we created Abayatique to share what we saw in the abaya: a flowing symbol of empowerment, grace, and identity, not just a garment but a statement. Our goal is to make it easier for women everywhere to discover that this beautiful piece of fashion is accessible, respected and loved by women of all walks of life. Every abaya will represent and reflect their unique style.',
                    'Abayatique isn’t just about shopping — it’s about discovering the stories behind each stitch, connecting with women-led brands, and learning about the art, culture, and expression that define the abaya. We want to support talented designers and aim to build a community rooted in culture and creativity. Every feature on our platform is designed with purpose — to give designers the tools to grow and give customers a seamless, joyful experience.',
                    'We’re inspired by women who prove every day that there is no one way to be powerful — every woman is special in her own way. And we’re here to celebrate that.',
                ],
            },
            {
                heading: 'Our Mission',
                paragraphs: [MISSION_STATEMENT],
            },
            {
                heading: 'Our Vision',
                paragraphs: [VISION_STATEMENT],
            },
        ],
    },

    mission: {
        title: 'Our Mission',
        sections: [
            {
                heading: 'Mission Statement',
                paragraphs: [MISSION_STATEMENT],
            },
            {
                heading: 'In Short',
                paragraphs: [MISSION_SHORT],
            },
            {
                heading: 'الرسالة',
                dir: 'rtl',
                lang: 'ar',
                paragraphs: [MISSION_STATEMENT_AR],
            },
            {
                heading: 'الرسالة القصيرة',
                dir: 'rtl',
                lang: 'ar',
                paragraphs: [MISSION_SHORT_AR],
            },
        ],
    },

    vision: {
        title: 'Our Vision',
        sections: [
            {
                heading: 'Vision Statement',
                paragraphs: [VISION_STATEMENT],
            },
            {
                heading: 'In Short',
                paragraphs: [VISION_SHORT],
            },
            {
                heading: 'الرؤية',
                dir: 'rtl',
                lang: 'ar',
                paragraphs: [VISION_STATEMENT_AR],
            },
            {
                heading: 'الرؤية القصيرة',
                dir: 'rtl',
                lang: 'ar',
                paragraphs: [VISION_SHORT_AR],
            },
        ],
    },

    collaboration: {
        title: 'Collaboration',
        sections: [
            {
                paragraphs: [
                    `${COMPANY_NAME} is built on the designers and sellers who list with us. If you make abayas or modest wear and want to reach customers across the UAE and the Gulf without building your own storefront, we would like to hear from you.`,
                    'Sellers on our platform keep their own identity and their own craft. We handle the storefront, the checkout, the delivery and the customer support, and we pass every customisation request straight through to you.',
                ],
            },
            {
                heading: 'Creators and Brand Partnerships',
                paragraphs: [
                    `We also work with photographers, stylists, creators and brands on campaigns and content. If you have an idea for a capsule collection or a partnership, ${COMPANY_NAME} would like to hear it.`,
                ],
            },
            {
                heading: 'How to Reach Us',
                paragraphs: [
                    `Send a short introduction, links to your work and what you have in mind to ${SUPPORT_EMAIL} with "Seller" or "Collaboration" in the subject line. We review every proposal and reply to those that fit.`,
                ],
            },
        ],
    },

    media: {
        title: 'Media',
        sections: [
            {
                paragraphs: [
                    `For press enquiries, interview requests, product samples or brand assets, contact our team at ${SUPPORT_EMAIL} with "Media" in the subject line.`,
                    'We can supply high-resolution imagery, logo files and background information on request. Please credit ' +
                        `${COMPANY_NAME} when using our material.`,
                ],
            },
        ],
    },

    'terms-and-conditions': {
        title: 'Terms and Conditions',
        sections: [
            {
                paragraphs: [
                    `By accessing this website you agree to these terms. If you do not agree with any part of them, please do not use the site.`,
                ],
            },
            {
                heading: 'Use of the Site',
                paragraphs: [
                    `This website and its content are owned by ${COMPANY_NAME}. You may browse the site for personal, non-commercial use. You may not copy, reproduce or redistribute our text, images or designs without written permission.`,
                ],
            },
            {
                heading: 'Accuracy of Information',
                paragraphs: [
                    'We take care to keep product descriptions, pricing and availability accurate, but we do not warrant that all content is error-free. We reserve the right to correct errors and to update information at any time without notice.',
                ],
            },
            {
                heading: 'Newsletter Subscriptions',
                paragraphs: [
                    'By subscribing you consent to receive occasional emails about our launch, collections and offers. You may unsubscribe at any time using the link in any email we send.',
                ],
            },
            {
                heading: 'Limitation of Liability',
                paragraphs: [
                    `To the fullest extent permitted by law, ${COMPANY_NAME} is not liable for any indirect or consequential loss arising from your use of this website.`,
                ],
            },
            {
                heading: 'Governing Law',
                paragraphs: [
                    'These terms are governed by the laws of the United Arab Emirates, and any dispute is subject to the exclusive jurisdiction of its courts.',
                ],
            },
        ],
    },

    'privacy-policy': {
        title: 'Privacy Policy',
        sections: [
            {
                paragraphs: [
                    `${COMPANY_NAME} respects your privacy. This policy explains what we collect, why, and what you can do about it.`,
                ],
            },
            {
                heading: 'What We Collect',
                paragraphs: [
                    'At this stage we collect only the email address you give us when subscribing to our newsletter. When ordering opens we will also collect the name, delivery address and contact details needed to fulfil an order.',
                ],
            },
            {
                heading: 'How We Use It',
                paragraphs: [
                    'We use your email solely to tell you about our launch, new collections and offers. We do not sell, rent or trade your personal information to third parties.',
                ],
            },
            {
                heading: 'Third-Party Processing',
                paragraphs: [
                    'Newsletter submissions on this site are delivered through FormSubmit, a third-party form service, which processes your email in order to forward it to us.',
                ],
            },
            {
                heading: 'Your Rights',
                paragraphs: [
                    `You may unsubscribe at any time, and you may ask us to tell you what data we hold about you or to delete it. Write to ${SUPPORT_EMAIL} and we will act on your request.`,
                ],
            },
            {
                heading: 'Cookies',
                paragraphs: [
                    'This site uses only the storage necessary for it to function, such as remembering that you have already seen our newsletter prompt during your visit.',
                ],
            },
        ],
    },

    'shipping-policy': {
        title: 'Shipping Policy',
        sections: [
            {
                paragraphs: [
                    `${COMPANY_NAME} is not yet shipping orders. The terms below will apply from the day we launch.`,
                ],
            },
            {
                heading: 'Delivery Areas',
                paragraphs: [
                    'We will deliver across the United Arab Emirates at launch, with wider GCC coverage to follow.',
                ],
            },
            {
                heading: 'Processing and Delivery Times',
                paragraphs: [
                    'Sellers process orders within 1 to 2 business days, and customised orders may take longer depending on the work involved — the timeline is confirmed with you before production begins. Delivery within the UAE typically takes a further 2 to 4 business days. You will receive a tracking link by email once your parcel leaves the seller.',
                ],
            },
            {
                heading: 'Shipping Charges',
                paragraphs: [
                    'Shipping charges are calculated at checkout based on your delivery address. Any customs duties or import taxes on international orders are the responsibility of the recipient.',
                ],
            },
            {
                heading: 'Delays',
                paragraphs: [
                    `Public holidays, peak periods and circumstances outside our control may affect delivery times. If your order is delayed, contact ${SUPPORT_EMAIL} and we will chase it for you.`,
                ],
            },
        ],
    },
};
